// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { NavLink, Link, useNavigate } from "react-router-dom"
import { useState, useEffect } from "react"
import { LayoutDashboard, ScrollText, AlertTriangle, BookOpen, Settings, LogOut, Wand2, Globe, Newspaper, Bot, FileText, Users, UserPlus, ClipboardList, Rocket, Library, Lock, Zap, Star, Home, Sparkles, ChevronDown, ChevronRight, Building2 } from "lucide-react"
import { useAuth } from "../context/AuthContext"
import { supabase } from "../lib/supabase"
import { GuardRokIcon } from "./GuardRokLogo"
import NotificationBell from "./NotificationBell"
import UptimeDot from "./UptimeDot"

// ── Free tier: always visible + functional ───────────────────────────────────
const freeNav = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/check",     icon: AlertTriangle,   label: "Pre-check Prompt" },
  { to: "/settings",  icon: Settings,        label: "Settings" },
]

// ── Paid tier: shown as locked items to free users ──────────────────────────
const paidNav = [
  { to: "/logs",                    icon: ScrollText,    label: "Prompt Logs" },
  { to: "/violations",              icon: AlertTriangle, label: "Violations" },
  { to: "/policies",                icon: BookOpen,      label: "Policies" },
  { to: "/compliance-hub",          icon: Globe,         label: "Compliance Hub" },
  { to: "/regulatory-intelligence", icon: Newspaper,     label: "Reg Intelligence" },
]

// ── Internal team — any is_internal user ────────────────────────────────────
const internalNav = [
  { to: "/onboarding",   icon: ClipboardList, label: "Onboarding" },
  { to: "/invite",       icon: UserPlus,      label: "Invite Team" },
]

// ── Exec-only — is_internal=true AND role=admin ──────────────────────────────
const execNav = [
  { to: "/wiki",         icon: Library,       label: "Wiki" },
  { to: "/compose",      icon: Sparkles,      label: "Compose" },
  { to: "/agents",       icon: Bot,           label: "Agents" },
  { to: "/team",         icon: Users,         label: "Team" },
  { to: "/handoff",      icon: FileText,      label: "Handoff" },
  { to: "/employee-book",icon: Users,         label: "Employee Book" },
  { to: "/post-beta",    icon: Rocket,        label: "Post-Beta Plan" },
]

// Dept → agent names (plain names only — no technical detail)
const DEPT_AGENT_NAMES: Record<string, string[]> = {
  engineering:        ["BASE", "FORGE"],
  "sales-marketing":  ["WADE", "IVY", "PENN", "SCOUT"],
  "legal-hr":         ["JUDE", "NOVA"],
  operations:         ["NOVA", "BASE"],
  payments:           ["CASH"],
  executive:          ["IRIS", "PAGE", "NOVA"],
  security:           ["GARD", "CLEO", "LEX"],
}

function deptToSlug(dept: string): string {
  const d = dept.toLowerCase()
  if (d.includes("engineer") || d.includes("product") || d.includes("tech")) return "engineering"
  if (d.includes("sales") || d.includes("market") || d.includes("growth"))   return "sales-marketing"
  if (d.includes("legal") || d.includes("hr") || d.includes("human"))        return "legal-hr"
  if (d.includes("ops") || d.includes("finance") || d.includes("operation")) return "operations"
  if (d.includes("payment") || d.includes("billing"))                         return "payments"
  if (d.includes("exec") || d.includes("founder") || d.includes("ceo"))      return "executive"
  if (d.includes("security"))                                                  return "security"
  return ""
}

function NavItem({ to, icon: Icon, label }: { to: string; icon: any; label: string }) {
  return (
    <NavLink to={to} end={to === "/"}
      className={({ isActive }) =>
        "flex items-center gap-2.5 px-5 py-2.5 text-sm font-medium border-l-2 transition-all " +
        (isActive ? "text-white bg-gray-900 border-red-500" : "text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-900")
      }>
      <Icon className="w-4 h-4" />{label}
    </NavLink>
  )
}

function AddonNavItem({ icon: Icon, label, to, unlocked }: { icon: any; label: string; to: string; unlocked: boolean }) {
  const navigate = useNavigate()
  if (unlocked) return <NavItem to={to} icon={Icon} label={label} />
  return (
    <button
      onClick={() => navigate("/settings?addon=policy-builder")}
      title="Add-on required"
      className="w-full flex items-center gap-2.5 px-5 py-2.5 text-sm font-medium border-l-2 border-transparent transition-all text-gray-600 cursor-pointer hover:bg-gray-900 hover:text-gray-500"
    >
      <Icon className="w-4 h-4 opacity-40" />
      <span className="flex-1 text-left opacity-40">{label}</span>
      <Star className="w-3 h-3 opacity-50 flex-shrink-0" style={{ color: "#f59e0b" }} />
    </button>
  )
}

function LockedNavItem({ icon: Icon, label }: { icon: any; label: string }) {
  const navigate = useNavigate()
  return (
    <button
      onClick={() => navigate("/settings?upgrade=1")}
      title="Upgrade to unlock"
      className="w-full flex items-center gap-2.5 px-5 py-2.5 text-sm font-medium border-l-2 border-transparent transition-all text-gray-600 cursor-pointer hover:bg-gray-900 hover:text-gray-500"
    >
      <Icon className="w-4 h-4 opacity-40" />
      <span className="flex-1 text-left opacity-40">{label}</span>
      <Lock className="w-3 h-3 opacity-50 flex-shrink-0" />
    </button>
  )
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const { profile, isInternal, isPaid, orgPlan, hasPolicyBuilder, isHR, grDisplayId, signOut } = useAuth()

  const [depts, setDepts]       = useState<string[]>([])
  const [openDept, setOpenDept] = useState<string | null>(null)

  useEffect(() => {
    if (!isInternal || !profile?.org_id) return
    supabase
      .from("users")
      .select("department")
      .eq("org_id", profile.org_id)
      .not("department", "is", null)
      .then(({ data }) => {
        const unique = [...new Set((data ?? []).map((r: any) => r.department).filter(Boolean))].sort() as string[]
        setDepts(unique)
      })
  }, [isInternal, profile?.org_id])

  return (
    <div className="flex min-h-screen bg-gray-100">
      <aside style={{width:"224px",background:"#030712"}} className="flex flex-col fixed inset-y-0 left-0 z-50">
        <div className="px-4 py-2 border-b border-gray-800">
          <UptimeDot />
        </div>
        <div className="px-5 py-4 border-b border-gray-800">
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
            <GuardRokIcon size={32} />
            <div>
              <div style={{fontFamily:"system-ui,sans-serif",fontWeight:800,fontSize:14,color:"#fff",letterSpacing:"-0.3px",lineHeight:1}}>
                Guard<span style={{color:"#E63946"}}>Rok</span><span style={{fontSize:8,color:"#9ca3af",verticalAlign:"super",marginLeft:1}}>™</span>
              </div>
              <div style={{fontSize:9,letterSpacing:"3px",color:"#6b7280",textTransform:"uppercase",marginTop:2}}>AI Firewall</div>
              <div style={{fontSize:7,color:"#374151",marginTop:1,letterSpacing:"0.5px"}}>Patent Pending Technology</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 py-3 overflow-y-auto">
          {/* Always-visible items */}
          {freeNav.map(item => <NavItem key={item.to} {...item} />)}

          {/* Analytics — hidden for role=user/hr (they see dedicated views) */}
          {profile?.role !== "user" && !isHR && (
            isPaid ? (
              <>
                <div style={{ margin: "10px 20px 4px", fontSize: 9, letterSpacing: "2px", color: "#374151", textTransform: "uppercase", fontWeight: 700 }}>
                  Analytics
                </div>
                {paidNav.map(item => <NavItem key={item.to} {...item} />)}
              </>
            ) : (
              <>
                <div style={{ margin: "10px 20px 4px", fontSize: 9, letterSpacing: "2px", color: "#374151", textTransform: "uppercase", fontWeight: 700 }}>
                  Analytics
                </div>
                {paidNav.map(item => <LockedNavItem key={item.to} icon={item.icon} label={item.label} />)}
              </>
            )
          )}

          {/* Add-ons — visible to paid admins/managers only */}
          {isPaid && !isHR && (
            <>
              <div style={{ margin: "10px 20px 4px", fontSize: 9, letterSpacing: "2px", color: "#374151", textTransform: "uppercase", fontWeight: 700 }}>
                Add-ons
              </div>
              <AddonNavItem to="/policy-builder" icon={Wand2} label="Policy Builder" unlocked={hasPolicyBuilder} />
            </>
          )}

          {/* Internal team — any is_internal user */}
          {isInternal && (
            <>
              <div style={{ margin: "10px 20px 4px", fontSize: 9, letterSpacing: "2px", color: "#374151", textTransform: "uppercase", fontWeight: 700 }}>
                Internal
              </div>
              {internalNav.map(item => <NavItem key={item.to} {...item} />)}
              {/* Exec-only links — only shown to admin+internal to match ExecRoute guard */}
              {profile?.role === "admin" && execNav.map(item => <NavItem key={item.to} {...item} />)}
            </>
          )}

          {/* Departments — collapsible, exec-only (links to /team which is ExecRoute) */}
          {isInternal && profile?.role === "admin" && depts.length > 0 && (
            <>
              <div style={{ margin: "10px 20px 4px", fontSize: 9, letterSpacing: "2px", color: "#374151", textTransform: "uppercase", fontWeight: 700 }}>
                Departments
              </div>
              {depts.map(dept => {
                const isOpen    = openDept === dept
                const slug      = deptToSlug(dept)
                const agentNames = DEPT_AGENT_NAMES[slug] ?? []
                return (
                  <div key={dept}>
                    <button
                      onClick={() => setOpenDept(isOpen ? null : dept)}
                      className="w-full flex items-center gap-2 border-l-2 border-transparent transition-all text-gray-500 hover:text-gray-300 hover:bg-gray-900"
                      style={{ padding: "6px 20px", fontSize: 12, fontWeight: 500 }}
                    >
                      <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="flex-1 text-left truncate">{dept}</span>
                      {isOpen
                        ? <ChevronDown className="w-3 h-3 flex-shrink-0" />
                        : <ChevronRight className="w-3 h-3 flex-shrink-0" />
                      }
                    </button>
                    {isOpen && (
                      <div style={{ borderLeft: "1px solid #1f2937", marginLeft: 28 }}>
                        <NavLink
                          to={`/team?dept=${encodeURIComponent(dept)}`}
                          className={({ isActive }) =>
                            "flex items-center gap-2 px-4 py-1.5 transition-all text-xs " +
                            (isActive ? "text-white" : "text-gray-500 hover:text-gray-300")
                          }
                        >
                          <Users className="w-3 h-3" /> People
                        </NavLink>
                        <NavLink
                          to="/wiki"
                          className="flex items-center gap-2 px-4 py-1.5 text-xs text-gray-500 hover:text-gray-300 transition-all"
                        >
                          <Library className="w-3 h-3" /> Wiki
                        </NavLink>
                        {agentNames.length > 0 && (
                          <div style={{ padding: "5px 16px 8px", display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {agentNames.map(a => (
                              <NavLink key={a} to="/agents"
                                style={{ fontSize: 9, fontWeight: 700, padding: "2px 5px", borderRadius: 3, background: "#111827", color: "#4b5563", textDecoration: "none", letterSpacing: "0.3px" }}
                              >
                                {a}
                              </NavLink>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </>
          )}
        </nav>

        {/* Upgrade nudge — only for free, non-internal users */}
        {!isPaid && !isInternal && (
          <div style={{ margin: "0 12px 8px", background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <Zap className="w-3 h-3" style={{ color: "#E63946" }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", letterSpacing: "0.3px" }}>Free plan</span>
            </div>
            <p style={{ fontSize: 10, color: "#6b7280", lineHeight: 1.5, marginBottom: 8 }}>
              Unlock logs, violations, policies &amp; compliance features.
            </p>
            <Link to="/settings?upgrade=1" style={{ display: "block", textAlign: "center", background: "#E63946", color: "#fff", fontSize: 10, fontWeight: 700, padding: "5px 0", borderRadius: 5, textDecoration: "none", letterSpacing: "0.5px" }}>
              UPGRADE
            </Link>
          </div>
        )}

        <div className="px-5 py-4 border-t border-gray-800">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-xs font-semibold text-gray-300">
              {profile?.full_name?.[0]??"?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-gray-200 text-xs font-medium truncate">{profile?.full_name??"User"}</div>
              <div className="text-gray-500 text-xs capitalize flex items-center gap-1">
                {orgPlan !== 'free' ? orgPlan : profile?.role ?? "admin"}
                {grDisplayId && <span className="font-mono opacity-60">· {grDisplayId}</span>}
              </div>
            </div>
            <NotificationBell />
            <Link to="/landing" title="Back to home page" className="text-gray-600 hover:text-gray-300 transition-colors" style={{ lineHeight: 0 }}>
              <Home className="w-4 h-4" />
            </Link>
            <button onClick={signOut} className="text-gray-600 hover:text-red-400 transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <main style={{marginLeft:"224px"}} className="flex-1 p-6">{children}</main>
    </div>
  )
}
